import { useState, useEffect } from 'react';
import { galleryAPI } from '../services/api';

export interface GalleryImage {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  category: string;
  featured: boolean;
  createdAt: Date;
}

interface GalleryFilters {
  category?: string;
  sortBy?: string;
  featured?: boolean;
  refreshTrigger?: number;
}

export const useGallery = (filters: GalleryFilters = {}) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  // Helper function to process API response and set state
  const processAndSetImages = (response: any) => {
    if (!response || !response.data || !Array.isArray(response.data)) {
      console.error('Invalid response format:', response);
      setError('Invalid response format from server');
      setImages([]);
      return;
    }
    
    // Get the origin for constructing absolute URLs
    const origin = window.location.origin;
    
    const imageList: GalleryImage[] = response.data.map((img: any) => {
      let imageUrl = img.imageUrl;
      
      // Ensure image URLs are absolute
      if (imageUrl && imageUrl.startsWith('/uploads/')) {
        // For uploads directory, use API path
        imageUrl = `${origin}/api${imageUrl}`;
      } else if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('//')) {
        imageUrl = `${origin}${imageUrl}`;
      }
      
      return {
        id: img._id,
        title: img.title || 'Untitled',
        description: img.description || '',
        imageUrl: imageUrl,
        category: img.category || '',
        featured: Boolean(img.featured),
        createdAt: new Date(img.createdAt || Date.now()),
      };
    });
    
    console.log('Processed images:', imageList.length, 'with featured:', 
      imageList.filter(img => img.featured).map(img => ({ id: img.id, title: img.title })));
    
    setImages(imageList);
    setCategories(response.categories || []);
  };

  // Function to directly clear and refresh images from the server
  const refreshImagesFromServer = async () => {
    setLoading(true);
    
    try {
      // Use a timestamp to bust cache
      const cacheBuster = Date.now() + Math.random();
      
      // In production, use the galleryAPI instead of direct URL construction
      // This avoids URL constructor issues completely
      try {
        console.log('Fetching gallery images via API...');
        const result = await galleryAPI.getImages(filters);
        
        if (result && result.data) {
          console.log('Got gallery images successfully via API:', {
            count: result.count,
            hasData: Array.isArray(result.data),
            itemCount: Array.isArray(result.data) ? result.data.length : 0
          });
          
          // Process the API result and return early
          processAndSetImages(result);
          return;
        }
      } catch (apiErr) {
        console.error('API method failed, falling back to direct fetch:', apiErr);
      }
      
      // Fallback to direct fetch if API method fails
      // This is a more reliable URL construction that works in all environments
      const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const origin = window.location.origin;
      
      // Safely join URL parts without double slashes
      const joinUrls = (base: string, path: string) => {
        // Remove trailing slash from base
        const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
        // Remove leading slash from path
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        return `${cleanBase}/${cleanPath}`;
      };
      
      let fullUrl;
      if (isAbsoluteUrl(API_URL)) {
        fullUrl = joinUrls(API_URL, 'gallery');
      } else {
        fullUrl = joinUrls(origin + API_URL, 'gallery');
      }
      
      console.log('Constructed URL for fetch:', fullUrl);
      
      // Use URLSearchParams separately to avoid URL constructor issues
      const params = new URLSearchParams();
      
      // Add all filters to URL
      if (filters.category) params.append('category', filters.category);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.featured) params.append('featured', 'true');
      
      // Add cache buster
      params.append('_t', cacheBuster.toString());
      
      // Combine URL and params
      const requestUrl = params.toString() ? `${fullUrl}?${params.toString()}` : fullUrl;
      console.log('Making direct fetch call to:', requestUrl);
      
      // Make a direct fetch call with cache control headers
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const fetchResponse = await fetch(requestUrl, { headers });
      if (!fetchResponse.ok) {
        throw new Error(`Failed to fetch images: ${fetchResponse.statusText}`);
      }
      
      const response = await fetchResponse.json();
      
      console.log('Fetch response data:', {
        success: response.success,
        count: response.count,
        featured: Array.isArray(response.data) && response.data.filter((img: any) => img.featured).length > 0
      });
      
      // Process the API result
      processAndSetImages(response);
      
    } catch (err) {
      console.error('Error in refreshImagesFromServer:', err);
      setError('Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  };

  // Call the refresh function whenever filters change
  useEffect(() => {
    console.log('Filters changed, refreshing images:', filters);
    refreshImagesFromServer();
  }, [filters.category, filters.sortBy, filters.featured, filters.refreshTrigger]);

  const deleteImage = async (imageId: string) => {
    try {
      await galleryAPI.deleteImage(imageId);
      return { success: true };
    } catch (err) {
      console.error('Error deleting image:', err);
      return { 
        success: false, 
        error: 'Failed to delete image' 
      };
    }
  };

  const updateImage = async (imageId: string, data: Partial<GalleryImage>) => {
    try {
      // Make sure to correctly convert the featured property for API call
      // Create a new object for the API that can have different types than GalleryImage
      let apiData: Record<string, any> = {...data};
      
      // Ensure boolean value is converted to a string that the server expects
      if (apiData.featured !== undefined) {
        apiData.featured = apiData.featured ? 'true' : 'false';
        
        // If toggling featured status, use the direct API method instead
        // This prevents conflicts with other updates
        console.log('Featured status detected in update, will be handled separately');
        delete apiData.featured;
      }
      
      // Only proceed with API call if there are properties to update
      if (Object.keys(apiData).length === 0) {
        console.log('No metadata to update after removing featured flag');
        return { success: true };
      }
      
      console.log('Sending update with data:', apiData);
      const result = await galleryAPI.updateImage(imageId, apiData);
      console.log('Update response:', result);
      return { success: true, data: result.data };
    } catch (err) {
      console.error('Error updating image:', err);
      return { 
        success: false, 
        error: 'Failed to update image' 
      };
    }
  };

  return { 
    images, 
    loading, 
    error, 
    categories,
    deleteImage,
    updateImage,
    refreshImagesFromServer
  };
};

export default useGallery;